/**
 * Labour cost is calculated admin-side because production users cannot read
 * userPrivateRates. This hook detects submitted tasks that are missing labour
 * cost and updates them automatically when an Admin views the dashboard.
 *
 * Flow:
 *  1. Query completed tasks where labourCostCalculatedAt == null
 *  2. Fetch all relevant userPrivateRates for the company
 *  3. Fetch adminSettings for defaultTeamHourlyRate + currency
 *  4. Call calcTaskMetrics() and updateDoc() for each pending task
 *
 * Historical stability: once labourCostCalculatedAt is set, this hook skips
 * the task. Admins can force a recalc via the edit flow, which always recalculates.
 */
import { useCallback, useRef, useState } from 'react';
import {
  collection, query, where, getDocs, doc, updateDoc,
  serverTimestamp, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { calcTaskMetrics } from '../utils/labour';
import { useAuth } from '../context/AuthContext';

export function useLabourCostCalculation() {
  const { companyId } = useAuth();
  const [calculating, setCalculating] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [lastUpdatedCount, setLastUpdatedCount] = useState(null);
  const runningRef = useRef(false);

  /**
   * @param {{ force?: boolean }} [opts]
   *   force=false (default): only process tasks never costed yet
   *     (labourCostCalculatedAt == null). Cheap; runs on dashboard mount.
   *   force=true: reprocess every still-uncosted task (labourCost == null),
   *     so newly entered rates retroactively cost previously-"unavailable"
   *     tasks. Tasks that already carry a cost are never in this set, so
   *     historical costs stay locked.
   */
  const runCalculation = useCallback(async (opts = {}) => {
    const force = opts === true || opts?.force === true;
    if (!companyId || runningRef.current) return;
    runningRef.current = true;
    setCalculating(true);

    try {
      // 1. Fetch adminSettings for currency + default team rate
      const adminSettingsSnap = await getDoc(doc(db, COLLECTIONS.ADMIN_SETTINGS, companyId));
      const adminSettings = adminSettingsSnap.exists() ? adminSettingsSnap.data() : {};
      const defaultTeamRate = adminSettings.defaultTeamHourlyRate ?? null;
      const currency = adminSettings.labourCostCurrency ?? 'CAD';

      // 2. Fetch all floor worker rates for this company. Rates live in the
      //    admin-only floorWorkerRates collection (keyed by floorWorkers doc
      //    ID) so production users can never read wages.
      const ratesSnap = await getDocs(
        query(collection(db, COLLECTIONS.FLOOR_WORKER_RATES), where('companyId', '==', companyId))
      );
      const userRates = {};
      ratesSnap.forEach((d) => {
        const data = d.data();
        if (data.hourlyRate != null) userRates[d.id] = data.hourlyRate;
      });

      // 3. Fetch completed tasks needing cost calculation.
      //    force: every task still without a cost (picks up newly entered rates)
      //    default: only tasks never processed yet
      const pendingSnap = await getDocs(
        query(
          collection(db, COLLECTIONS.PRODUCTION_TASKS),
          where('companyId', '==', companyId),
          where('status', '==', 'completed'),
          where('archived', '==', false),
          where(force ? 'labourCost' : 'labourCostCalculatedAt', '==', null)
        )
      );

      if (pendingSnap.empty) {
        setLastUpdatedCount(0);
        setLastRun(new Date());
        return;
      }

      // 4. Calculate and update each pending task
      const now = new Date();
      const updates = pendingSnap.docs.map(async (taskDoc) => {
        const task = taskDoc.data();
        // teamSize=0 can occur when a task was started in individual mode with no
        // floor workers in the system yet. Treat as 1 so the default team rate
        // (or individual rates if employeeIds are present) can still be applied.
        const effectiveTeamSize = task.teamSize > 0
          ? task.teamSize
          : (task.employeeIds?.length > 0 ? task.employeeIds.length : 1);
        const metrics = calcTaskMetrics(
          { ...task, teamSize: effectiveTeamSize },
          userRates, defaultTeamRate, currency, now,
        );

        await updateDoc(doc(db, COLLECTIONS.PRODUCTION_TASKS, taskDoc.id), {
          ...metrics,
          updatedAt: serverTimestamp(),
        });
      });

      await Promise.all(updates);
      setLastUpdatedCount(pendingSnap.docs.length);
      setLastRun(new Date());
    } catch (err) {
      console.error('[Floora] Labour cost calculation failed:', err);
    } finally {
      runningRef.current = false;
      setCalculating(false);
    }
  }, [companyId]);

  return { calculating, lastRun, lastUpdatedCount, runCalculation };
}
